import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Table from '../common/Table';
import RTFilters from '../common/RTFilters';
import RTFilteredTable from '../common/RTFilteredTable';
import RTButton from '../common/RTButton';
import { apiGet } from '../../api/apiClient';
import { toastError } from '../common/toast';
import { OCCUPANCY_GENDER_FILTER_OPTIONS } from '../../utils/occupancyConstants';
import '../common/rtCommon.css';
import './ViewVacantBeds.css';
import RTPageLayout from '../common/RTPageLayout';

const ViewVacantBeds = () => {
  const [vacantBeds, setVacantBeds] = useState([]);
  const [totalVacantBeds, setTotalVacantBeds] = useState(0);
  const [buildings, setBuildings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    building_id: 'all',
    room_type: undefined,
    occupancy_gender: undefined,
  });
  const navigate = useNavigate();
  const location = useLocation();

  const filterFields = useMemo(
    () => [
      {
        key: 'building_id',
        label: 'Building',
        type: 'dropdown',
        width: 'medium',
        options: [
          { value: 'all', label: 'All Buildings' },
          ...buildings.map((building) => ({
            value: String(building.building_id),
            label: building.name,
          })),
        ],
        applyImmediately: true,
      },
      {
        key: 'room_type',
        label: 'Room Type',
        type: 'dropdown',
        width: 'compact',
        options: [
          { value: '', label: 'All' },
          { value: 'AC', label: 'AC' },
          { value: 'Non AC', label: 'Non AC' },
        ],
        applyImmediately: true,
      },
      {
        key: 'occupancy_gender',
        label: 'Gender',
        type: 'dropdown',
        width: 'compact',
        options: OCCUPANCY_GENDER_FILTER_OPTIONS,
        applyImmediately: true,
      },
    ],
    [buildings],
  );

  useEffect(() => {
    fetchBuildings();
    fetchVacantBeds({
      building_id: 'all',
      room_type: undefined,
      occupancy_gender: undefined,
    });
  }, []);

  const fetchBuildings = async () => {
    try {
      const response = await apiGet('/building/names');
      if (response.status === 200) {
        setBuildings(response.data || []);
      }
    } catch (error) {
      console.error('Error fetching buildings:', error);
      toastError('Failed to fetch buildings');
    }
  };

  const fetchVacantBeds = async (filterParams = filters) => {
    try {
      setLoading(true);
      const buildingId = filterParams.building_id ?? 'all';
      const params = new URLSearchParams();
      if (filterParams.room_type) {
        params.append('room_type', filterParams.room_type);
      }
      if (filterParams.occupancy_gender) {
        params.append('occupancy_gender', filterParams.occupancy_gender);
      }
      const query = params.toString();
      const baseUrl =
        buildingId === 'all'
          ? '/building/vacant_beds'
          : `/building/vacant_beds/${buildingId}`;
      const url = query ? `${baseUrl}?${query}` : baseUrl;

      const response = await apiGet(url);
      if (response.status === 200) {
        const data = response.data || {};
        setVacantBeds(data.vacant_beds || []);
        setTotalVacantBeds(data.total_vacant_beds || 0);
        if (!data.vacant_beds || data.vacant_beds.length === 0) {
          toastError('No vacant beds found');
        }
      }
    } catch (error) {
      console.error('Error fetching vacant beds:', error);
      toastError('Failed to fetch vacant beds');
      setVacantBeds([]);
      setTotalVacantBeds(0);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    const next = {
      ...filters,
      [key]: key === 'building_id' ? value || 'all' : value || undefined,
    };
    setFilters(next);
    fetchVacantBeds(next);
  };

  const appliedFilterPills = useMemo(() => {
    const pills = [];
    if (filters.building_id !== 'all') {
      const buildingLabel =
        buildings.find(
          (b) => String(b.building_id) === String(filters.building_id),
        )?.name || filters.building_id;
      pills.push({ key: 'building_id', label: `Building: ${buildingLabel}` });
    }
    if (filters.room_type) {
      pills.push({
        key: 'room_type',
        label: `Room Type: ${filters.room_type}`,
      });
    }
    if (filters.occupancy_gender) {
      const genderLabel =
        OCCUPANCY_GENDER_FILTER_OPTIONS.find(
          (o) => o.value === filters.occupancy_gender,
        )?.label || filters.occupancy_gender;
      pills.push({ key: 'occupancy_gender', label: `Gender: ${genderLabel}` });
    }
    return pills;
  }, [filters, buildings]);

  const handleRemoveAppliedFilter = (key) => {
    const next = {
      ...filters,
      [key]: key === 'building_id' ? 'all' : undefined,
    };
    setFilters(next);
    fetchVacantBeds(next);
  };

  const handleClearAllAppliedFilters = () => {
    const next = {
      building_id: 'all',
      room_type: undefined,
      occupancy_gender: undefined,
    };
    setFilters(next);
    fetchVacantBeds(next);
  };

  const getTableTitle = () => {
    if (filters.building_id === 'all') {
      return `Vacant Beds - All Buildings (${totalVacantBeds} Total)`;
    }
    const buildingName =
      buildings.find(
        (b) => String(b.building_id) === String(filters.building_id),
      )?.name || '';
    return `Vacant Beds - ${buildingName} (${totalVacantBeds} Total)`;
  };

  const handleViewBuilding = (row) => {
    navigate(`/building/view/${row.building_id}`);
  };

  const handleAllocateBed = (row) => {
    navigate('/bedallocation/allocate-bed', {
      state: {
        selectedBed: row,
      },
    });
  };

  const handleBack = () => {
    navigate(-1);
  };

  const columns = [
    {
      Header: 'Building',
      accessor: 'building_name',
    },
    {
      Header: 'Room',
      accessor: 'room_name',
    },
    {
      Header: 'Vacant Beds',
      accessor: 'vacant_beds',
      Cell: ({ row }) => (
        <span className="vacant-beds-count">
          {row.vacant_beds} of {row.number_of_beds}
        </span>
      ),
      exportValue: (row) => `${row.vacant_beds} of ${row.number_of_beds}`,
    },
    {
      Header: 'Room Mates',
      accessor: 'room_mates',
      Cell: ({ value }) =>
        value && value.length > 0 ? value.join(', ') : 'None',
      exportValue: (row) =>
        row.room_mates && row.room_mates.length > 0
          ? row.room_mates.join(', ')
          : 'None',
    },
    {
      Header: 'Bed Rent',
      accessor: 'bed_rent',
      Cell: ({ value }) => `₹${value}`,
    },
    {
      Header: 'Action',
      accessor: 'action',
      Cell: ({ row }) => (
        <div style={{ display: 'flex', gap: '8px' }}>
          <RTButton
            style={{ width: 'auto' }}
            type="link"
            onClick={() => handleViewBuilding(row)}
          >
            View
          </RTButton>
          <RTButton
            type="link"
            permissionKey="bedmanagement.allocate_bed"
            onClick={() => handleAllocateBed(row)}
          >
            Allocate Bed
          </RTButton>
        </div>
      ),
      exportValue: () => '',
    },
  ];

  return (
    <RTPageLayout
      activePath={location.pathname}
      title="View Vacant Beds"
      actions={
        <>
          <RTButton variant="secondary" onClick={handleBack}>
            &#8592; Back
          </RTButton>
        </>
      }
      mainSection={true}
    >
      <RTFilteredTable
        loading={loading}
        loadingMessage="Loading vacant beds..."
        filters={
          <RTFilters
            fields={filterFields}
            values={filters}
            onChange={handleFilterChange}
            pills={appliedFilterPills}
            onRemovePill={handleRemoveAppliedFilter}
            onClearAll={handleClearAllAppliedFilters}
          />
        }
      >
        <Table
          title={getTableTitle()}
          columns={columns}
          data={vacantBeds}
          showSearch={true}
          exportable={true}
          exportFileName="vacant_beds"
          showPagination={true}
          noDataText="No vacant beds available"
        />
      </RTFilteredTable>
    </RTPageLayout>
  );
};

export default ViewVacantBeds;
